import { type MouseEvent, memo, useCallback } from 'react';

import { CheckIcon, ChevronRightIcon, EllipsisIcon, PinIcon, ScanSearchIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { DropdownMenu, type DropdownMenuOption, renderDropdownItems } from '@air/primitive-dropdown-menu';
import { HoverCard } from '@air/primitive-hover-card';
import { IconButton } from '@air/primitive-icon-button';
import { cn } from '@air/utils-tailwind';

import { getSaveAsBoardActionLabel } from '~/components/v2/components/workspace/workspace-agent-asset-list/workspaceAgentAssetListConstants';
import { WorkspaceAgentPanelImage } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPanelImage';
import {
  WORKSPACE_AGENT_HOVER_CLOSE_DELAY_MS,
  WORKSPACE_AGENT_HOVER_OPEN_DELAY_MS,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentHoverDelays';
import { WORKSPACE_AGENT_PANEL_ARTIFACT_THUMBNAIL } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelImageDimensions';
import { WorkspaceAgentAssetListHoverPanel } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/WorkspaceAgentAssetListHoverPanel';
import { type WorkspaceAgentThumbnail } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/workspaceAgentThumbnailTypes';

const MOSAIC_MAX_THUMBNAILS = 4;
const MOSAIC_TRIPLE_COUNT = 3;

export type WorkspaceAgentAssetListArtifactCardProps = {
  artifactId: string;
  count: number;
  isPinned?: boolean;
  onOpenList?: () => void;
  onOpenThumbnail?: (id: string) => void;
  onPinToThread?: () => void;
  onSaveAsBoard?: () => void;
  onShowInThread?: () => void;
  onUnpin?: () => void;
  savedBoardTitle?: string | null;
  showSaveActions?: boolean;
  thumbnails: WorkspaceAgentThumbnail[];
  title?: string;
  unavailableCount: number;
};

type MosaicThumbnailsProps = {
  thumbnails: WorkspaceAgentThumbnail[];
  title: string;
};

const MosaicThumbnails = memo(({ thumbnails, title }: MosaicThumbnailsProps) => {
  const validThumbnails = thumbnails
    .filter((item): item is WorkspaceAgentThumbnail & { thumbnailUrl: string } =>
      Boolean(!item.isUnavailable && item.thumbnailUrl),
    )
    .slice(0, MOSAIC_MAX_THUMBNAILS);

  if (validThumbnails.length === 0) {
    return (
      <span className="flex size-14 shrink-0 items-center justify-center ads:bg-subtle bg-gray-3 ads:text-icon-subtle text-gray-8">
        <ScanSearchIcon aria-hidden size="sm" />
      </span>
    );
  }

  if (validThumbnails.length === 1) {
    return (
      <span className="relative size-14 shrink-0 overflow-hidden ads:bg-subtle bg-gray-3">
        <WorkspaceAgentPanelImage
          {...WORKSPACE_AGENT_PANEL_ARTIFACT_THUMBNAIL}
          alt={title}
          className="size-full object-cover object-center"
          src={validThumbnails[0].thumbnailUrl}
        />
      </span>
    );
  }

  if (validThumbnails.length === 2) {
    return (
      <span className="grid size-14 shrink-0 grid-cols-2 gap-px overflow-hidden ads:bg-subtle bg-gray-3">
        {validThumbnails.map((item, index) => (
          <WorkspaceAgentPanelImage
            {...WORKSPACE_AGENT_PANEL_ARTIFACT_THUMBNAIL}
            alt={`${title} ${index + 1}`}
            className="size-full object-cover object-center"
            key={`${item.id}-${index}`}
            src={item.thumbnailUrl}
          />
        ))}
      </span>
    );
  }

  if (validThumbnails.length === MOSAIC_TRIPLE_COUNT) {
    return (
      <span className="grid size-14 shrink-0 grid-cols-2 grid-rows-2 gap-px overflow-hidden ads:bg-subtle bg-gray-3">
        <WorkspaceAgentPanelImage
          {...WORKSPACE_AGENT_PANEL_ARTIFACT_THUMBNAIL}
          alt={`${title} 1`}
          className="row-span-2 size-full object-cover object-center"
          src={validThumbnails[0].thumbnailUrl}
        />
        <WorkspaceAgentPanelImage
          {...WORKSPACE_AGENT_PANEL_ARTIFACT_THUMBNAIL}
          alt={`${title} 2`}
          className="size-full object-cover object-center"
          src={validThumbnails[1].thumbnailUrl}
        />
        <WorkspaceAgentPanelImage
          {...WORKSPACE_AGENT_PANEL_ARTIFACT_THUMBNAIL}
          alt={`${title} 3`}
          className="size-full object-cover object-center"
          src={validThumbnails[2].thumbnailUrl}
        />
      </span>
    );
  }

  return (
    <span className="grid size-14 shrink-0 grid-cols-2 grid-rows-2 gap-px overflow-hidden ads:bg-subtle bg-gray-3">
      {validThumbnails.map((item, index) => (
        <WorkspaceAgentPanelImage
          {...WORKSPACE_AGENT_PANEL_ARTIFACT_THUMBNAIL}
          alt={`${title} ${index + 1}`}
          className="size-full object-cover object-center"
          key={`${item.id}-${index}`}
          src={item.thumbnailUrl}
        />
      ))}
    </span>
  );
});

MosaicThumbnails.displayName = 'MosaicThumbnails';

const getDefaultArtifactTitle = (thumbnails: WorkspaceAgentThumbnail[], count: number) => {
  const firstAvailable = thumbnails.find((item) => !item.isUnavailable) ?? thumbnails[0];

  if (!firstAvailable) {
    return count === 1 ? '1 asset' : `${count} assets`;
  }

  if (count <= 1) {
    return firstAvailable.label;
  }

  const rest = count - 1;

  return `${firstAvailable.label} and ${rest} ${rest === 1 ? 'other' : 'others'}`;
};

export const WorkspaceAgentAssetListArtifactCard = memo(
  ({
    artifactId,
    count,
    isPinned = false,
    onOpenList,
    onOpenThumbnail,
    onPinToThread,
    onSaveAsBoard,
    onShowInThread,
    onUnpin,
    savedBoardTitle,
    showSaveActions = true,
    thumbnails,
    title,
    unavailableCount,
  }: WorkspaceAgentAssetListArtifactCardProps) => {
    const resolvedTitle = title?.trim() || getDefaultArtifactTitle(thumbnails, count);
    const baseSubtitle = count === 1 ? '1 item' : `${count.toLocaleString()} items`;
    const subtitle = unavailableCount > 0 ? `${baseSubtitle} · ${unavailableCount} unavailable` : baseSubtitle;
    const isNavigable = Boolean(onOpenList);

    const handleSaveClick = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        onSaveAsBoard?.();
      },
      [onSaveAsBoard],
    );

    const handlePinClick = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        onPinToThread?.();
      },
      [onPinToThread],
    );

    const rowContent = (
      <>
        <MosaicThumbnails thumbnails={thumbnails} title={resolvedTitle} />
        <div className="flex min-w-0 flex-1 items-start gap-2.5 py-2 pr-2.5 pl-3">
          <div className="flex min-w-0 flex-1 flex-col justify-center">
            <span className="truncate font-medium ads:text-primary text-12 text-gray-12 leading-4">
              {resolvedTitle}
            </span>
            <span className="mt-0.5 flex min-w-0 items-center gap-0.5 ads:text-metadata text-12 text-gray-9 leading-4">
              <span className="truncate">{subtitle}</span>
              <ChevronRightIcon
                aria-hidden
                className={cn(
                  'shrink-0 ads:text-icon-subtle text-gray-8 transition-all group-hover:translate-x-px',
                  isNavigable ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
                )}
                size="xs"
              />
            </span>
          </div>
          <span className="mt-0.5 shrink-0 ads:text-icon-subtle text-gray-8">
            <ScanSearchIcon aria-hidden size="sm" />
          </span>
        </div>
      </>
    );

    const rowShellClass = cn(
      'ads:border-subtle ads:bg-base hover:ads:border-default hover:ads:bg-hover-subtle group flex w-full min-w-0 items-center overflow-hidden rounded-6 border border-gray-3 bg-gray-1 text-left transition-colors hover:border-gray-5 hover:bg-gray-2',
      isNavigable
        ? 'focus-visible:ads:ring-focus cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-altitude-9'
        : 'cursor-default',
    );

    const rowNode = isNavigable ? (
      <button
        aria-label={resolvedTitle}
        className={rowShellClass}
        data-testid="WORKSPACE_AGENT_MESSAGE_ASSET_LIST_PILL_TRIGGER"
        onClick={onOpenList}
        type="button"
      >
        {rowContent}
      </button>
    ) : (
      <div className={rowShellClass}>{rowContent}</div>
    );

    const hasHoverThumbnails = thumbnails.length > 0;
    const hasPinnedMenuActions = Boolean(onUnpin || onShowInThread);

    const cardWithHover =
      hasHoverThumbnails && !hasPinnedMenuActions ? (
        <HoverCard
          className="w-auto min-w-0 overflow-hidden p-0"
          closeDelay={WORKSPACE_AGENT_HOVER_CLOSE_DELAY_MS}
          openDelay={WORKSPACE_AGENT_HOVER_OPEN_DELAY_MS}
          side="top"
          sideOffset={6}
          trigger={rowNode}
        >
          <WorkspaceAgentAssetListHoverPanel
            onOpenList={onOpenList}
            onOpenThumbnail={onOpenThumbnail}
            thumbnails={thumbnails}
            title={resolvedTitle}
            unavailableCount={unavailableCount}
          />
        </HoverCard>
      ) : (
        rowNode
      );

    return (
      <div
        className="group/pin relative flex w-full min-w-0 flex-col items-start gap-1.5"
        data-pinned-item={artifactId}
        data-testid="WORKSPACE_AGENT_MESSAGE_ASSET_LIST_PILL"
      >
        {cardWithHover}

        {hasPinnedMenuActions ? (
          <div className="absolute top-1 right-8 z-10 opacity-0 transition-opacity focus-within:opacity-100 group-hover/pin:opacity-100">
            <DropdownMenu
              trigger={
                <IconButton
                  appearance="ghost"
                  className="ads:bg-base bg-gray-1"
                  color="gray"
                  data-testid="WORKSPACE_AGENT_PINNED_ITEM_ACTIONS"
                  label="Pinned item actions"
                  size="small"
                >
                  <EllipsisIcon aria-hidden size="sm" />
                </IconButton>
              }
            >
              {renderDropdownItems({
                options: [
                  ...(onUnpin
                    ? [
                        {
                          id: 'unpin',
                          label: 'Unpin',
                          onSelect: onUnpin,
                          type: 'item',
                        } satisfies DropdownMenuOption,
                      ]
                    : []),
                  ...(onShowInThread
                    ? [
                        {
                          id: 'show-in-thread',
                          label: 'Show in thread',
                          onSelect: onShowInThread,
                          type: 'item',
                        } satisfies DropdownMenuOption,
                      ]
                    : []),
                ],
              })}
            </DropdownMenu>
          </div>
        ) : null}

        {showSaveActions && (onSaveAsBoard || onPinToThread) ? (
          <div className="flex flex-wrap items-center gap-1.5" data-testid="WORKSPACE_AGENT_ASSET_LIST_ACTIONS">
            {onSaveAsBoard ? (
              <Button
                appearance="outline"
                className="shrink-0 gap-1 font-medium"
                color="gray"
                data-testid="WORKSPACE_AGENT_ASSET_LIST_SAVE_AS_BOARD"
                disabled={savedBoardTitle != null}
                onClick={handleSaveClick}
                size="small"
                type="button"
              >
                {savedBoardTitle != null ? <CheckIcon aria-hidden size="xs" /> : null}
                {getSaveAsBoardActionLabel(savedBoardTitle)}
              </Button>
            ) : null}
            {onPinToThread ? (
              <Button
                appearance="outline"
                className="shrink-0 gap-1 font-medium"
                color="gray"
                data-testid="WORKSPACE_AGENT_ASSET_LIST_PIN_TO_THREAD"
                disabled={isPinned}
                onClick={handlePinClick}
                size="small"
                type="button"
              >
                {isPinned ? <CheckIcon aria-hidden size="xs" /> : <PinIcon aria-hidden size="xs" />}
                {isPinned ? 'Pinned' : 'Pin to thread'}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  },
);

WorkspaceAgentAssetListArtifactCard.displayName = 'WorkspaceAgentAssetListArtifactCard';
